<template lang="pug">
  .sort
    template(v-if='checkbox')
      .sort-item(
        v-for='option in options'
        @click="setSort(option.value)"
        :key='option.value'
        role="button"
        tabindex="0"
        @keydown.enter.prevent="setSort(option.value)"
        @keydown.space.prevent="setSort(option.value)"
      )
        .link {{ option.label }}
    RogueSelect(
      v-else
      id="sort"
      :options="options"
      :modelValue="selectedValue"
      @update:modelValue="setSort($event)"
    )
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useRogue } from '@/js/services/Rogue'

const DEFAULT_SORT_VALUE = 'featured'

const DEFAULT_OPTIONS = [
  { label: 'Featured', value: 'featured' },
  { label: 'Name', value: 'name' },
  { label: 'Price: Low to High', value: 'price.asc' },
  { label: 'Price: High to Low', value: 'price.desc' },
  { label: 'Newest', value: 'newest' },
]

const route = useRoute()
const rogue = useRogue()
const router = useRouter()
const emit = defineEmits<{ (e: 'select'): void }>()

const props = withDefaults(defineProps<{
  checkbox?: boolean
  value?: string
  sortOptions?: { label: string; value: string; selected: boolean }[]
}>(), {
  checkbox : false,
  value    : DEFAULT_SORT_VALUE,
})

const options = computed(() => {
  if (props.sortOptions) {
    return props.sortOptions
  }

  return DEFAULT_OPTIONS
})

// Resolves the URL sort value to the matching option value, handling mismatches
// between URL format and option format in either direction:
//   "newest"      → "newest.desc"  (URL has no direction, option does)
//   "newest.desc" → "newest"       (URL has direction, option doesn't)
const normalizedValue = computed(() => {
  const val = props.value
  const opts = options.value

  if (opts.find(o => o.value === val)) return val

  if (val.includes('.')) {
    const prefix = val.split('.')[0]
    
    return opts.find(o => o.value === prefix)?.value ?? val
  }

  return opts.find(o => o.value.startsWith(val + '.'))?.value ?? val
})

const selectedValue = computed(() => {
  if (props.sortOptions) {
    return props.sortOptions.find(o => o.value === normalizedValue.value)?.value
      ?? props.sortOptions.find(o => o.selected)?.value
      ?? normalizedValue.value
  }

  return normalizedValue.value
})

const isApplying = ref(false)

function setSort(val: string) {
  if (isApplying.value) {
    return
  }
  if (normalizedValue.value === val) {
    emit('select')

    return
  }
  isApplying.value = true
  const query = { ...route.query }
  query.sort = val
  delete query.page_number
  router.push({ query })
  // Slight delay so the route update doesn't race the drawer close
  setTimeout(() => emit('select'), 0)
  // Prevent rapid double-taps flipping the order
  setTimeout(() => { isApplying.value = false }, 500)
}
</script>

<style lang='scss' scoped>
  .sort-item {
    font-size: $size-xs;
    line-height: $line-height-l;
    margin-bottom: $space-xxs;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    :deep(.link) {
      display: block;
      color: inherit;
      text-decoration: none;
    }
  }

  .sort :deep(.field.select) {
    margin-bottom: 0;
  }
</style>
